import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import ajax from '@/lib/ajax';
import CreateProductViewModel from '@/models/Products/Wizard/CreateProductViewModel';
import FeeDefinition from '@/models/Products/ProductFees/FeeDefinition';
import CreateProductRequest from '@/models/Products/Wizard/CreateProductRequest';
import CreateGeneralLedgerMappingsRequest from '@/models/Products/Wizard/CreateGeneralLedgerMappingsRequest';
import { AppModule } from '@/store/Auth/AppModule';
import ListUtils from '@/lib/listUtils';
import RateSource from '@/models/Products/RateSources/RateSource';
import CurrencyUtils from '@/lib/currencyUtils';
import PeriodType from '@/models/Shared/PeriodType';

export interface IProductWizardState {
    loading: boolean;
    loadingWizard: boolean;
    loadingDialog: boolean;
    createProductViewModel: CreateProductViewModel;
    rateSources: RateSource[];
}

@Module({ dynamic: true, store, name: 'ProductWizardModule', namespaced: true })
class ProductWizardStore extends VuexModule implements IProductWizardState {

    public loading: boolean = true;
    public loadingWizard: boolean = true;
    public loadingDialog: boolean = true;
    public createProductViewModel: CreateProductViewModel = new CreateProductViewModel();
    public rateSources: RateSource[] = [];

    @Action
    public async cleanState() {
        this.context.commit("resetState");
    }

    @Action
    public async createProduct() {

        this.setLoadingWizard(true);

        // Create request and set the product general settings
        const request = new CreateProductRequest();
        request.generalSettings = this.createProductViewModel.generalSettings;

        // Set the product sub type specific settings depending on the feature visibilty settings
        const featureVisibility = ListUtils.productFeatureVisibility(request.generalSettings.subType);
        if (featureVisibility.generalPayeeSettings && this.createProductViewModel.generalPayeeSettingsAvailable) {
            request.generalPayeeSettings = this.createProductViewModel.generalPayeeSettings;
            request.generalPayeeSettings.maximumNumberOfPayees = +request.generalPayeeSettings.maximumNumberOfPayees;
        }
        if (featureVisibility.depositBankSettings && this.createProductViewModel.depositBankSettingsAvailable) {
            request.depositBankSettings = this.createProductViewModel.depositBankSettings;
        }
        if (featureVisibility.depositCardSettings && this.createProductViewModel.depositCardSettingsAvailable) {
            request.depositCardSettings = this.createProductViewModel.depositCardSettings;
        }
        if (featureVisibility.depositPaymentOrderSettings && this.createProductViewModel.depositPaymentOrderSettingsAvailable) {
            request.depositPaymentOrderSettings = this.createProductViewModel.depositPaymentOrderSettings;
            // TODO: Seems to be some tech debt on the backend here where we MUST set one/two/three approval threshold amounts even if they are not enabled, waste of code
            request.depositPaymentOrderSettings.oneApprovalThreshold.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositPaymentOrderSettings.oneApprovalThreshold.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositPaymentOrderSettings.oneApprovalThreshold.amount);
            request.depositPaymentOrderSettings.twoApprovalThreshold.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositPaymentOrderSettings.twoApprovalThreshold.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositPaymentOrderSettings.twoApprovalThreshold.amount);
            request.depositPaymentOrderSettings.threeApprovalThreshold.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositPaymentOrderSettings.threeApprovalThreshold.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositPaymentOrderSettings.threeApprovalThreshold.amount);
            if (request.depositPaymentOrderSettings.noticePeriodEnabled) {
                request.depositPaymentOrderSettings.noticePeriodValue = +request.depositPaymentOrderSettings.noticePeriodValue;
            }
        }
        if (featureVisibility.depositInterestSettings && this.createProductViewModel.depositInterestSettingsAvailable) {
            request.depositInterestSettings = this.createProductViewModel.depositInterestSettings;
            request.depositInterestSettings.interestBearingThreshold.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositInterestSettings.interestBearingThreshold.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositInterestSettings.interestBearingThreshold.amount);
            request.depositInterestSettings.accruedPeriodValue = 1; // Hardcoded to 1
            request.depositInterestSettings.accruedPeriodType = PeriodType.Day; // Hardcoded to day
            request.depositInterestSettings.postedPeriodValue = +request.depositInterestSettings.postedPeriodValue;
            request.depositInterestSettings.rateValue = +request.depositInterestSettings.rateValue;
        }
        if (featureVisibility.depositFixedTermSettings && this.createProductViewModel.depositFixedTermSettingsAvailable) {
            request.depositFixedTermSettings = this.createProductViewModel.depositFixedTermSettings;
            request.depositFixedTermSettings.termPeriodValue = +request.depositFixedTermSettings.termPeriodValue
            request.depositFixedTermSettings.initialDepositWindowPeriodValue = +request.depositFixedTermSettings.initialDepositWindowPeriodValue;
            request.depositFixedTermSettings.fundingWindowPeriodValue = +request.depositFixedTermSettings.fundingWindowPeriodValue;
            request.depositFixedTermSettings.minimumBalance.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositFixedTermSettings.minimumBalance.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositFixedTermSettings.minimumBalance.amount);
            request.depositFixedTermSettings.maximumBalance.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositFixedTermSettings.maximumBalance.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositFixedTermSettings.maximumBalance.amount);
        }
        if (featureVisibility.depositFixedTermMaturitySettings && this.createProductViewModel.depositFixedTermMaturitySettingsAvailable) {
            request.depositFixedTermMaturitySettings = this.createProductViewModel.depositFixedTermMaturitySettings;
            request.depositFixedTermMaturitySettings.maturityWindowPeriodValue = +request.depositFixedTermMaturitySettings.maturityWindowPeriodValue;
        }
        if (featureVisibility.depositOverdraftSettings && this.createProductViewModel.depositOverdraftSettingsAvailable) {
            request.depositOverdraftSettings = this.createProductViewModel.depositOverdraftSettings;
            request.depositOverdraftSettings.hardLimit.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositOverdraftSettings.hardLimit.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositOverdraftSettings.hardLimit.amount);
            request.depositOverdraftSettings.softLimit.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositOverdraftSettings.softLimit.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositOverdraftSettings.softLimit.amount);
        }
        if (featureVisibility.depositOverdraftInterestSettings && this.createProductViewModel.depositOverdraftInterestSettingsAvailable) {
            request.depositOverdraftInterestSettings = this.createProductViewModel.depositOverdraftInterestSettings;
            request.depositOverdraftInterestSettings.accruedPeriodValue = 1; // Hardcoded to 1
            request.depositOverdraftInterestSettings.accruedPeriodType = PeriodType.Day; // Hardcoded to day
            request.depositOverdraftInterestSettings.postedPeriodValue = +request.depositOverdraftInterestSettings.postedPeriodValue;
            request.depositOverdraftInterestSettings.rateValue = +request.depositOverdraftInterestSettings.rateValue;
            request.depositOverdraftInterestSettings.interestBearingThreshold.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositOverdraftInterestSettings.interestBearingThreshold.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositOverdraftInterestSettings.interestBearingThreshold.amount);
        }
        if (featureVisibility.depositInstantAccessSettings && this.createProductViewModel.depositInstantAccessSettings) {
            request.depositInstantAccessSettings = this.createProductViewModel.depositInstantAccessSettings;
            request.depositInstantAccessSettings.initialDepositWindowPeriodValue = +request.depositInstantAccessSettings.initialDepositWindowPeriodValue;
            request.depositInstantAccessSettings.fundingWindowPeriodValue = +request.depositInstantAccessSettings.fundingWindowPeriodValue;
            request.depositInstantAccessSettings.minimumBalance.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositInstantAccessSettings.minimumBalance.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositInstantAccessSettings.minimumBalance.amount);
            request.depositInstantAccessSettings.maximumBalance.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.depositInstantAccessSettings.maximumBalance.amount = CurrencyUtils.convertDecimalStringToAmountString(request.depositInstantAccessSettings.maximumBalance.amount);
        }
        if (featureVisibility.depositCoolingOffSettings && this.createProductViewModel.depositCoolingOffSettings) {
            request.depositCoolingOffSettings = this.createProductViewModel.depositCoolingOffSettings;
            request.depositCoolingOffSettings.coolingOffPeriodValue = +request.depositCoolingOffSettings.coolingOffPeriodValue;
        }
        if (featureVisibility.lendingInterestSettings && this.createProductViewModel.lendingInterestSettings) {
            request.lendingInterestSettings = this.createProductViewModel.lendingInterestSettings;
            request.lendingInterestSettings.rateValue = +request.lendingInterestSettings.rateValue;
            request.lendingInterestSettings.rateFloorValue = +request.lendingInterestSettings.rateFloorValue;
            request.lendingInterestSettings.rateCeilingValue = +request.lendingInterestSettings.rateCeilingValue;
            request.lendingInterestSettings.rateChargedPeriodValue = +request.lendingInterestSettings.rateChargedPeriodValue;
            request.lendingInterestSettings.accruedPeriodValue = +request.lendingInterestSettings.accruedPeriodValue;
        }
        if (featureVisibility.lendingScheduleSettings && this.createProductViewModel.lendingScheduleSettings) {
            request.lendingScheduleSettings = this.createProductViewModel.lendingScheduleSettings;
            request.lendingScheduleSettings.paymentIntervalPeriodValue = +request.lendingScheduleSettings.paymentIntervalPeriodValue;
            request.lendingScheduleSettings.numberOfInstallments = +request.lendingScheduleSettings.numberOfInstallments;
            request.lendingScheduleSettings.numberOfInstallmentsMinimum = +request.lendingScheduleSettings.numberOfInstallmentsMinimum;
            request.lendingScheduleSettings.numberOfInstallmentsMaximum = +request.lendingScheduleSettings.numberOfInstallmentsMaximum;
            request.lendingScheduleSettings.firstDueDateOffsetPeriodValue = +request.lendingScheduleSettings.firstDueDateOffsetPeriodValue;
            request.lendingScheduleSettings.firstDueDateOffsetPeriodValueMinimum = +request.lendingScheduleSettings.firstDueDateOffsetPeriodValueMinimum;
            request.lendingScheduleSettings.firstDueDateOffsetPeriodValueMaximum = +request.lendingScheduleSettings.firstDueDateOffsetPeriodValueMaximum;
            request.lendingScheduleSettings.gracePeriodValue = +request.lendingScheduleSettings.gracePeriodValue;
        }
        if (featureVisibility.lendingTermSettings && this.createProductViewModel.lendingTermSettings) {
            request.lendingTermSettings = this.createProductViewModel.lendingTermSettings;
            request.lendingTermSettings.termPeriodValue = +request.lendingTermSettings.termPeriodValue;
            request.lendingTermSettings.amount.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.lendingTermSettings.amount.amount = CurrencyUtils.convertDecimalStringToAmountString(request.lendingTermSettings.amount.amount);
            request.lendingTermSettings.amountMinimum.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.lendingTermSettings.amountMinimum.amount = CurrencyUtils.convertDecimalStringToAmountString(request.lendingTermSettings.amountMinimum.amount);
            request.lendingTermSettings.amountMaximum.currency = this.createProductViewModel.generalSettings.defaultWalletCurrency;
            request.lendingTermSettings.amountMaximum.amount = CurrencyUtils.convertDecimalStringToAmountString(request.lendingTermSettings.amountMaximum.amount);
        }
        if (featureVisibility.lendingRepaymentPlanSettings && this.createProductViewModel.lendingRepaymentPlanSettings) {
            request.lendingRepaymentPlanSettings = this.createProductViewModel.lendingRepaymentPlanSettings;
        }

        await ajax.post(`/portal-backend/fi-api/internal/products`, request)
        .then(response => {

            const newProductId = response.data.id;
            this.context.commit("setNewProductId", newProductId);

            // If general ledger settings were activated then we also need to create the general ledger mappings
            if (this.createProductViewModel.generalLedgerSettingsAvailable) {

                // Go to a separate endpoint to create the general ledger mappings
                const generalLedgerRequest = new CreateGeneralLedgerMappingsRequest();
                generalLedgerRequest.transactionSourceGLId = this.createProductViewModel.generalLedgerSettings.transactionSourceGLId;
                generalLedgerRequest.savingsPortfolioControlGLId = this.createProductViewModel.generalLedgerSettings.savingsPortfolioControlGLId;
                generalLedgerRequest.savingsInterestExpenseGLId = this.createProductViewModel.generalLedgerSettings.savingsInterestExpenseGLId;
                generalLedgerRequest.savingsInterestPayableGLId = this.createProductViewModel.generalLedgerSettings.savingsInterestPayableGLId;
                generalLedgerRequest.savingsFeeIncomeGLId = this.createProductViewModel.generalLedgerSettings.savingsFeeIncomeGLId;

                ajax.post(`/portal-backend/fi-api/internal/products/${newProductId}/general-ledger-mapping-defaults`, generalLedgerRequest)
                .then(() => {
                    // Nothing else needs to be saved or set after doing this
                    this.setLoadingWizard(false);
                })
                .catch(error => {
                    AppModule.handleApiError(error);
                    this.setLoadingWizard(false);
                });
            }
            else {
                this.setLoadingWizard(false);
            }
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingWizard(false);
        });
    }

    @Action
    public async createFeeDefinition(feeDefinition: FeeDefinition) {
        this.context.commit("setFeeDefinition", feeDefinition);
    }

    @Action
    async getRateSources() {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/rate-sources/`)
        .then(response => {
            this.context.commit("setProductWizardRateSources", response.data.results);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    public async clearProductViewModel() {
        this.context.commit("resetCreateProductViewModel");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingWizard = false;
        this.loadingDialog = true;
        this.createProductViewModel = new CreateProductViewModel();
        this.rateSources = [];
    }

    @Mutation
    private async resetCreateProductViewModel() {
        this.createProductViewModel = new CreateProductViewModel();
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingWizard(loading: boolean) {
        this.loadingWizard = loading;
    }

    @Mutation
    private async setNewProductId(id: string) {
        this.createProductViewModel.newProductId = id;
    }

    @Mutation
    private async setFeeDefinition(feeDefinition: FeeDefinition) {
        this.createProductViewModel.generalFeeSettings.feeDefinitions.push(feeDefinition);
    }

    @Mutation
    private async setProductWizardRateSources(rateSources: RateSource[]) {
        if (rateSources) {
            this.rateSources = rateSources;
        }
    }

}

export const ProductWizardModule = getModule(ProductWizardStore);